import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import DataTable from "react-data-table-component";
import axiosInstance from "../axiosConfig";
import Swal from "sweetalert2";
import { Upload, Trash2, Search, CheckCircle, AlertCircle, FileText, Clock, Settings, Edit, CheckSquare, Plus, X, UserCheck, DownloadCloud, Image } from "lucide-react";
import "../assets/CSS/Dashboard.css";
import OCRQtyMasterModal from "../components/OCRQtyMasterModal";

export default function PurchaseInvoice() {
  const user = { Auth: "Admin" }; 

  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);

  const [activeTab, setActiveTab] = useState("ALL"); 
  const [invoiceList, setInvoiceList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isExporting, setIsExporting] = useState(false);

  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [promptText, setPromptText] = useState("");
  const [isSavingPrompt, setIsSavingPrompt] = useState(false);

  const [showVendorModal, setShowVendorModal] = useState(false);
  const [vendorSearchName, setVendorSearchName] = useState("");
  const [isVerifyingVendor, setIsVerifyingVendor] = useState(false);
  const [vendorVerifyResult, setVendorVerifyResult] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showOcrQtyModal, setShowOcrQtyModal] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editFormData, setEditFormData] = useState({
    id: null,
    partyName: "",
    poNumber: "",
    totalAmount: "",
    items: []
  });

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/api/purchase/bill/list`);
      setInvoiceList(res.data || []);
    } catch (err) {
      Swal.fire({ icon: "error", title: "Fetch Failed", text: "Something went wrong" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvoices();
  }, []);

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!uploadFile) return;

    const formData = new FormData();
    formData.append("file", uploadFile);

    let progressInterval;
    try {
      setIsUploading(true);
      setUploadProgress(0);
      
      progressInterval = setInterval(() => {
        setUploadProgress(prev => (prev >= 95 ? prev : prev + Math.floor(Math.random() * 5) + 2));
      }, 1000);

      const res = await axiosInstance.post("/api/purchase/bill/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      setTimeout(() => {
        setOcrResult(res.data);
        setShowUploadModal(false);
        setUploadFile(null);
        setUploadProgress(0);
        setIsUploading(false);
        loadInvoices(); 
      }, 600);
      
    } catch (err) {
      clearInterval(progressInterval);
      setUploadProgress(0);
      setIsUploading(false);
      let errorMsg = err.response?.data?.message || "OCR Processing failed";
      Swal.fire({ icon: "error", title: "Action Required", text: errorMsg, confirmButtonColor: "#ef4444" });
    }
  };

  const handleApprove = async (id) => {
    Swal.fire({ title: 'Approve this invoice?', text: "Once approved, it cannot be deleted or edited.", icon: 'question', showCancelButton: true }).then(async (res) => {
      if(res.isConfirmed) {
        try {
          const response = await axiosInstance.put(`/api/purchase/bill/approve/${id}`);
          Swal.fire('Approved!', response.data.message || 'Invoice approved successfully.', 'success');
          loadInvoices();
        } catch (err) {
          Swal.fire('Error', err.response?.data?.message || 'Failed to approve invoice.', 'error');
        }
      }
    });
  };

  const handleDelete = (id) => {
    Swal.fire({ title: 'Delete record?', icon: 'warning', showCancelButton: true }).then(async (res) => {
      if(res.isConfirmed) {
        await axiosInstance.delete(`/api/purchase/bill/delete/${id}`);
        loadInvoices();
      }
    });
  };

  /* ================= EXPORT HANDLER ================= */
  const handleExportExcel = async (exportId = null) => {
    try {
      setIsExporting(true);
      const url = exportId ? `/api/purchase/bill/export?id=${exportId}` : '/api/purchase/bill/export';
      const res = await axiosInstance.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', exportId ? `Purchase_Bill_${exportId}.xlsx` : `Approved_Purchase_Bills_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (err) {
      Swal.fire("Export Failed", "Failed to download the Excel file.", "error");
    } finally {
      setIsExporting(false);
    }
  };

  const fetchPrompt = async () => {
    try {
      const res = await axiosInstance.get("/api/purchase/settings/prompt");
      setPromptText(res.data.prompt);
      setShowSettingsModal(true);
    } catch (err) {
      Swal.fire("Error", "Could not fetch AI prompt settings.", "error");
    }
  };

  const handleSavePrompt = async () => {
    try {
      setIsSavingPrompt(true);
      await axiosInstance.post("/api/purchase/settings/prompt", { promptText });
      Swal.fire("Saved", "AI Prompt updated successfully!", "success");
      setShowSettingsModal(false);
    } catch (err) {
      Swal.fire("Error", "Failed to update prompt.", "error");
    } finally {
      setIsSavingPrompt(false);
    }
  };

  const handleVerifyVendor = async (e) => {
    e.preventDefault();
    if (!vendorSearchName.trim()) return;
    try {
      setIsVerifyingVendor(true);
      setVendorVerifyResult(null);
      const res = await axiosInstance.post("/api/purchase/vendor/verify", { vendorName: vendorSearchName });
      setVendorVerifyResult(res.data);
    } catch (err) {
      Swal.fire("Error", err.response?.data?.message || "Failed to search vendor.", "error");
    } finally {
      setIsVerifyingVendor(false);
    }
  };

  /* ================= EDIT HANDLERS (AUTO CALCULATION) ================= */
  const handleEditOpen = (row) => {
    let parsedItems = [];
    try {
      const rawParsed = JSON.parse(row.ExtractedItems || "[]");
      parsedItems = Array.isArray(rawParsed) ? rawParsed : (rawParsed.items || []);
    } catch (e) { parsedItems = []; }

    setEditFormData({
      id: row.ID,
      partyName: row.PartyName || "",
      poNumber: row.PONumber || "",
      totalAmount: row.TotalAmount || "",
      items: parsedItems
    });
    setShowEditModal(true);
  };

  const handleEditItemChange = (index, field, value) => {
    const newItems = [...editFormData.items];
    newItems[index][field] = value;

    // AUTO CALCULATION LOGIC: Amount = Qty * Rate
    if (field === "qty" || field === "rate") {
      const q = parseFloat(newItems[index].qty) || 0;
      const r = parseFloat(newItems[index].rate) || 0;
      newItems[index].amount = (q * r).toFixed(2);
    }

    // Auto Calculate Grand Total
    const newTotalAmount = newItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);

    setEditFormData({ 
      ...editFormData, 
      items: newItems, 
      totalAmount: newTotalAmount.toFixed(2) 
    });
  };

  const handleAddItemRow = () => {
    setEditFormData({
      ...editFormData,
      items: [...editFormData.items, { name: "", qty: "", rate: "", amount: "0.00" }]
    });
  };

  const handleRemoveItemRow = (index) => {
    const newItems = editFormData.items.filter((_, i) => i !== index);
    const newTotalAmount = newItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    setEditFormData({ ...editFormData, items: newItems, totalAmount: newTotalAmount.toFixed(2) });
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsUpdating(true);
      await axiosInstance.put(`/api/purchase/bill/update/${editFormData.id}`, editFormData);
      
      // Call details API silently to trigger backend status recalculation & auto-update
      await axiosInstance.get(`/api/purchase/bill/details/${editFormData.id}`);
      
      Swal.fire("Updated", "Record updated successfully!", "success");
      setShowEditModal(false);
      loadInvoices(); 
    } catch (err) {
      Swal.fire("Error", "Failed to update record.", "error");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleViewDetails = async (row) => {

    try {

      const res = await axiosInstance.get(
        `/api/purchase/bill/details/${row.ID}`
      );

      const details = res.data;

      let parsedItems = [];

      try {

        parsedItems = JSON.parse(
          details.ExtractedItems || "{}"
        );

        parsedItems = Array.isArray(parsedItems)
          ? parsedItems
          : (parsedItems.items || []);

      } catch (e) {

        parsedItems = [];

      }

      setOcrResult({
        ID: details.ID,
        status: details.Status,
        mismatchDetails: details.MismatchReasons,
        data: {
          partyName: details.PartyName,
          poNumber: details.PONumber,
          date: details.BillDate
            ? new Date(details.BillDate)
                .toISOString()
                .split("T")[0]
            : "N/A",
          totalAmount: details.TotalAmount,
          items: parsedItems
        }
      });

      // Reload invoices in background so the list reflects any DB auto-updates (e.g. status changes)
      loadInvoices();

      window.scrollTo({
        top: 0,
        behavior: "smooth"
      });

    } catch (err) {

      Swal.fire(
        "Error",
        err.response?.data?.message ||
        "Failed to fetch details",
        "error"
      );

    }

  };

  const columns = [
    { 
      name: "Vendor / Party", 
      selector: row => row.PartyName, 
      sortable: true, 
      cell: row => (
        <div className="py-2" onClick={() => handleViewDetails(row)} style={{ cursor: "pointer" }} title="Click to view full details">
          <b className="text-primary text-decoration-underline d-block">{row.PartyName}</b>
          <small className="text-muted">ID: #{row.ID}</small>
        </div> 
      ) 
    },
    {
      name: "Items Detail",
      grow: 2,
      cell: row => {
        try {
          const parsedData = JSON.parse(row.ExtractedItems || "[]");
          const itemsList = Array.isArray(parsedData) ? parsedData : (parsedData.items || []);

          return (
            <div className="py-2 w-100">
              {itemsList.length > 0 ? (
                itemsList.map((it, i) => (
                  <div key={i} className="mb-2 p-2 bg-light rounded border-start border-primary border-3" style={{fontSize: '11px'}}>
                    <div className="fw-bold text-dark">{it.name || it.description || 'Unknown Item'}</div>
                    <div className="d-flex justify-content-between text-muted mt-1">
                      <span>{it.qty || 0} x {Number(it.rate || 0).toLocaleString('en-IN', {minimumFractionDigits:2, maximumFractionDigits:2})}</span>
                      <span className="fw-bold text-primary">₹{Number(it.amount || 0).toLocaleString('en-IN', {minimumFractionDigits:2, maximumFractionDigits:2})}</span>
                    </div>
                  </div>
                ))
              ) : ( <span className="text-muted fst-italic" style={{fontSize: '11px'}}>No items found</span> )}
            </div>
          );
        } catch (e) { return "-"; }
      }
    },
    { name: "PO Number", selector: row => row.PONumber || "N/A", sortable: true },
    { 
      name: "Status", 
      cell: row => (
        <span className={`badge ${row.Status === 'APPROVED' ? 'bg-primary' : (row.Status === 'GREEN' ? 'bg-success' : 'bg-danger')} d-flex align-items-center gap-1`}>
          {row.Status === 'APPROVED' ? <CheckSquare size={12}/> : (row.Status === 'GREEN' ? <CheckCircle size={12}/> : <AlertCircle size={12}/>)}
          {row.Status === 'APPROVED' ? 'APPROVED' : (row.Status === 'GREEN' ? 'VERIFIED' : 'NOT VERIFIED')}
        </span>
      )
    },
    { name: "Date", selector: row => new Date(row.CreatedAt).toLocaleDateString("en-GB") },
    {
      name: "Action",
      minWidth: "120px",
      cell: row => (
        <div className="d-flex gap-3 align-items-center">
          {row.Status === 'APPROVED' ? (
            <span className="text-muted small fw-bold"><CheckSquare size={14} className="me-1"/> Done</span>
          ) : (
            <>
              <button onClick={() => handleEditOpen(row)} className="btn btn-sm text-primary p-0" title="Edit Record"><Edit size={18}/></button>
              <button onClick={() => handleApprove(row.ID)} className="btn btn-sm text-success p-0" title="Approve Record"><CheckSquare size={18}/></button>
              <button onClick={() => handleDelete(row.ID)} className="btn btn-sm text-danger p-0" title="Delete Record"><Trash2 size={18}/></button>
            </>
          )}
        </div>
      )
    }
  ];

  const displayedInvoices = invoiceList.filter(item => {
    if (activeTab === "NOT VERIFIED") return item.Status === 'RED';
    if (activeTab === "VERIFIED") return item.Status === 'GREEN';
    if (activeTab === "APPROVED") return item.Status === 'APPROVED';
    return true;
  });

  const filteredData = displayedInvoices.filter(item => 
    item.PartyName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.PONumber?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const stats = {
    total: invoiceList.length,
    pending: invoiceList.filter(i => i.Status === 'RED').length,
    verified: invoiceList.filter(i => i.Status === 'GREEN').length,
    approved: invoiceList.filter(i => i.Status === 'APPROVED').length,
  };

  return (
    <div className="d-flex dashboard bg-light">
      <div className={isSidebarVisible ? "sidebar-container" : "sidebar-hidden"}>
        <Sidebar />
      </div>

      <div className="flex-grow-1">
        <Header toggleSidebar={toggleSidebar} isSidebarVisible={isSidebarVisible} />

        <main className="main-container p-4 text-start">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h3 className="fw-bold mb-0">Purchase Bill Intelligence</h3>
              <p className="text-muted small">OCR Extraction & Database Validation</p>
            </div>
            
            <div className="d-flex gap-3 align-items-center">
              
              {/* EXPORT BUTTON */}
              <button onClick={() => handleExportExcel()} disabled={isExporting} className="btn btn-success shadow-sm d-flex align-items-center gap-2 text-white">
                <DownloadCloud size={18}/> {isExporting ? "Exporting..." : "Export Excel"}
              </button>

              <button onClick={() => { setShowVendorModal(true); setVendorSearchName(""); setVendorVerifyResult(null); }} className="btn btn-light shadow-sm border d-flex align-items-center gap-2">
                <UserCheck size={18} className="text-primary"/> Vendor & PO Lookup
              </button>

              {(user?.Auth === "Admin" || user?.Auth === "SuperAdmin") && (
                <>
                  <button onClick={fetchPrompt} className="btn btn-light shadow-sm border d-flex align-items-center gap-2">
                    <Settings size={18} className="text-secondary"/> AI Prompt
                  </button>
                  <button onClick={() => setShowOcrQtyModal(true)} className="btn btn-light shadow-sm border d-flex align-items-center gap-2 text-decoration-none text-dark">
                    <Settings size={18} className="text-secondary"/> OCR Qty Master
                  </button>
                </>
              )}

              {!ocrResult && (
                <button onClick={() => setShowUploadModal(true)} className="btn btn-indigo shadow-sm d-flex align-items-center gap-2" style={{backgroundColor: '#4f46e5', color: 'white'}}>
                  <Upload size={18}/> Upload New Bill
                </button>
              )}
            </div>
          </div>

          {/* ================= CONDITIONAL RENDERING ================= */}
          {ocrResult ? (
            <div className="animate__animated animate__fadeIn">
              <InvoiceResultView ocrResponse={ocrResult} onExport={handleExportExcel} isExporting={isExporting} />
              <div className="d-flex justify-content-end gap-3 mt-4">
                <button onClick={() => setOcrResult(null)} className="btn btn-secondary px-4 py-2 shadow-sm fw-bold">
                  Close Result & Back to Dashboard
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* KPI Cards */}
              <div className="row mb-4 g-3">
                <div className="col-md-3">
                  <div className="card border-0 shadow-sm p-3">
                    <div className="d-flex align-items-center gap-3">
                      <div className="p-3 bg-light rounded-3 text-primary"><FileText /></div>
                      <div><h6 className="text-muted small fw-bold mb-0 text-uppercase">Total Volume</h6><h3 className="fw-bold mb-0">{stats.total}</h3></div>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card border-0 shadow-sm p-3 border-start border-danger border-4">
                    <div className="d-flex align-items-center gap-3">
                      <div className="p-3 bg-danger-light rounded-3 text-danger"><Clock /></div>
                      <div><h6 className="text-danger small fw-bold mb-0 text-uppercase tracking-wider">Not Verified</h6><h3 className="fw-bold mb-0 text-danger">{stats.pending}</h3></div>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card border-0 shadow-sm p-3 border-start border-success border-4">
                    <div className="d-flex align-items-center gap-3">
                      <div className="p-3 bg-success-light rounded-3 text-success"><CheckCircle /></div>
                      <div><h6 className="text-success small fw-bold mb-0 text-uppercase tracking-wider">Verified</h6><h3 className="fw-bold mb-0 text-success">{stats.verified}</h3></div>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card border-0 shadow-sm p-3 border-start border-primary border-4">
                    <div className="d-flex align-items-center gap-3">
                      <div className="p-3 rounded-3 text-primary" style={{backgroundColor: '#e0e7ff'}}><CheckSquare /></div>
                      <div><h6 className="text-primary small fw-bold mb-0 text-uppercase tracking-wider">Approved</h6><h3 className="fw-bold mb-0 text-primary">{stats.approved}</h3></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Status Meaning Highlight / Legend */}
              <div className="row g-3 mb-4">
                <div className="col-md-4">
                  <div className="d-flex align-items-start gap-3 p-3 rounded-4 h-100 shadow-sm" style={{backgroundColor: '#fef2f2', border: '1px solid #fca5a5'}}>
                    <Clock size={20} className="text-danger mt-1 flex-shrink-0"/>
                    <div>
                      <strong className="text-danger small text-uppercase tracking-wider">Not Verified</strong> 
                      <p className="text-danger small mb-0 mt-1 opacity-75" style={{fontSize: '12.5px', lineHeight: '1.4'}}>AI extracted data, but items didn't match the PO. Needs your manual check & edit.</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="d-flex align-items-start gap-3 p-3 rounded-4 h-100 shadow-sm" style={{backgroundColor: '#f0fdf4', border: '1px solid #86efac'}}>
                    <CheckCircle size={20} className="text-success mt-1 flex-shrink-0"/>
                    <div>
                      <strong className="text-success small text-uppercase tracking-wider">Verified</strong> 
                      <p className="text-success small mb-0 mt-1 opacity-75" style={{fontSize: '12.5px', lineHeight: '1.4'}}>AI perfectly matched the extracted items with the PO. Waiting for your final approval.</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="d-flex align-items-start gap-3 p-3 rounded-4 h-100 shadow-sm" style={{backgroundColor: '#eff6ff', border: '1px solid #93c5fd'}}>
                    <CheckSquare size={20} className="text-primary mt-1 flex-shrink-0"/>
                    <div>
                      <strong className="text-primary small text-uppercase tracking-wider">Approved</strong> 
                      <p className="text-primary small mb-0 mt-1 opacity-75" style={{fontSize: '12.5px', lineHeight: '1.4'}}>You have locked and approved this bill. It is now ready to be exported to Tally.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Data Table Block */}
              <div className="card p-3 shadow-sm border-0 rounded-4">
                <div className="d-flex gap-2 mb-4 bg-light p-1 rounded-3 w-fit">
                  {[
                    { id: "ALL", label: `ALL (${stats.total})` },
                    { id: "NOT VERIFIED", label: `NOT VERIFIED (${stats.pending})` },
                    { id: "VERIFIED", label: `VERIFIED (${stats.verified})` },
                    { id: "APPROVED", label: `APPROVED (${stats.approved})` }
                  ].map(tab => (
                    <button 
                      key={tab.id}
                      className={`btn btn-sm px-4 rounded-2 fw-bold transition-all ${activeTab === tab.id ? "text-white shadow" : "text-muted border-0"}`} 
                      style={activeTab === tab.id ? {backgroundColor: '#4f46e5'} : {}}
                      onClick={() => setActiveTab(tab.id)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                <div className="mb-3 position-relative" style={{maxWidth: '350px'}}>
                  <span className="position-absolute top-50 translate-middle-y ms-3 text-muted"><Search size={16}/></span>
                  <input 
                    className="form-control ps-5 border-light bg-light" 
                    placeholder="Search Vendor or PO..." 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>

                <DataTable
                  columns={columns}
                  data={filteredData}
                  pagination
                  progressPending={loading}
                  highlightOnHover
                  responsive
                  customStyles={{
                    headCells: { style: { fontWeight: '800', color: '#64748b', textTransform: 'uppercase', fontSize: '11px' } },
                    rows: { style: { minHeight: '70px', borderBottom: '1px solid #f1f5f9' } }
                  }}
                />
              </div>
            </>
          )}

        </main>
      </div>

      {/* ================= VENDOR VERIFY MODAL ================= */}
      {showVendorModal && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', zIndex: 1055}}>
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
              <div className="modal-header border-0 bg-white px-4 pt-4 pb-2">
                <h5 className="fw-black mb-0 d-flex align-items-center gap-3 text-dark">
                  <div className="p-2 bg-primary bg-opacity-10 text-primary rounded-circle d-flex"><UserCheck size={22} /></div>
                  Vendor & PO Lookup
                </h5>
                <button type="button" className="btn-close shadow-none" onClick={() => setShowVendorModal(false)}></button>
              </div>
              <form onSubmit={handleVerifyVendor}>
                <div className="modal-body px-4 pb-4 pt-3">
                  <div className="input-group input-group-lg mb-4 shadow-sm rounded-3 overflow-hidden border">
                    <span className="input-group-text bg-light border-0 text-muted ps-3 pr-2"><Search size={20} /></span>
                    <input type="text" className="form-control border-0 bg-light shadow-none fs-6" placeholder="Search Vendor Party or PO Number..." value={vendorSearchName} onChange={(e) => setVendorSearchName(e.target.value)} required />
                    <button type="submit" className="btn btn-primary px-4 fw-bold" disabled={isVerifyingVendor}>{isVerifyingVendor ? "Searching..." : "Search"}</button>
                  </div>
                  {vendorVerifyResult && (
                    <div className="animate__animated animate__fadeIn">
                      {vendorVerifyResult.found ? (
                        <div className="bg-success bg-opacity-10 border border-success border-opacity-25 rounded-4 p-3">
                          <div className="d-flex align-items-center gap-2 mb-3">
                            <CheckCircle size={20} className="text-success"/>
                            <h6 className="fw-bold text-success mb-0">{vendorVerifyResult.message}</h6>
                          </div>
                          {vendorVerifyResult.matches && vendorVerifyResult.matches.length > 0 && (
                            <div className="mt-3">
                              <h6 className="fw-bold text-muted small text-uppercase mb-2 px-1">Tally Ledger Matches</h6>
                              <div className="bg-white rounded-3 shadow-sm border" style={{maxHeight: '200px', overflowY: 'auto', overflowX: 'hidden'}}>
                                <div className="list-group list-group-flush">
                                  {vendorVerifyResult.matches.map((name, idx) => (
                                    <div key={idx} className="list-group-item d-flex align-items-center gap-3 py-2 border-bottom text-dark hover-bg-light transition-all">
                                      <div className="bg-light rounded-circle d-flex align-items-center justify-content-center text-muted border flex-shrink-0" style={{width: '28px', height: '28px'}}>
                                        <span className="fw-bold" style={{fontSize: '11px'}}>{idx + 1}</span>
                                      </div>
                                      <span className="fw-medium" style={{fontSize: '13px', wordBreak: 'break-word'}}>{name}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {vendorVerifyResult.poMatches && vendorVerifyResult.poMatches.length > 0 && (
                            <div className="mt-3">
                              <h6 className="fw-bold text-muted small text-uppercase mb-2 px-1">PO Database Matches</h6>
                              <div className="bg-white rounded-3 shadow-sm border" style={{maxHeight: '450px', overflowY: 'auto'}}>
                                <div className="table-responsive">
                                  <table className="table table-hover table-bordered mb-0" style={{fontSize: '0.8rem'}}>
                                    <thead className="table-dark sticky-top">
                                      <tr>
                                        <th className="py-2 text-white fw-bold">PO Number</th>
                                        <th className="py-2 text-white fw-bold">Supplier</th>
                                        <th className="py-2 text-white fw-bold">Item Name</th>
                                        <th className="py-2 text-white fw-bold">Item Desc</th>
                                        <th className="py-2 text-white fw-bold">Group</th>
                                        <th className="py-2 text-white fw-bold text-end">Rate</th>
                                        <th className="py-2 text-white fw-bold text-end">PO Qty</th>
                                        <th className="py-2 text-white fw-bold text-end">Amount</th>
                                        <th className="py-2 text-white fw-bold text-end">Override</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {vendorVerifyResult.poMatches.map((po, idx) => (
                                        <tr key={idx} className="align-middle">
                                          <td className="py-2 fw-bold text-primary">{po.poNumber}</td>
                                          <td className="py-2">{po.supplierName}</td>
                                          <td className="py-2">{po.itemName}</td>
                                          <td className="py-2 text-muted">{po.itemDesc}</td>
                                          <td className="py-2">{po.groupName || '-'}</td>
                                          <td className="py-2 text-end fw-bold">₹{po.rate}</td>
                                          <td className="py-2 text-end">{po.qty}</td>
                                          <td className="py-2 text-end text-success fw-bold">₹{po.amount || '0.00'}</td>
                                          <td className="py-2 text-end">
                                            {po.qtyOverride ? (
                                              <span className="badge bg-warning text-dark px-2 py-1">{po.qtyOverride}</span>
                                            ) : (
                                              <span className="text-muted">-</span>
                                            )}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="bg-danger bg-opacity-10 border border-danger border-opacity-25 rounded-4 p-4 text-center">
                          <div className="mb-2"><AlertCircle size={36} className="text-danger opacity-75"/></div>
                          <h6 className="fw-bold text-danger mb-1">Vendor Not Found</h6>
                          <p className="small text-danger opacity-75 mb-0">{vendorVerifyResult.message}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ================= EDIT RECORD MODAL (READ ONLY AMOUNTS) ================= */}
      {showEditModal && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055}}>
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
              <div className="modal-header border-0 bg-primary text-white p-4">
                <h5 className="fw-bold mb-0 d-flex align-items-center gap-2"><Edit size={20}/> Manual Edit Mode</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowEditModal(false)}></button>
              </div>
              
              <form onSubmit={handleUpdateSubmit}>
                <div className="modal-body p-4 bg-light">
                  <div className="row g-3 mb-4">
                    <div className="col-md-5">
                      <label className="fw-bold text-muted small mb-1 text-uppercase">Vendor Name</label>
                      <input type="text" className="form-control fw-bold" value={editFormData.partyName} onChange={(e) => setEditFormData({...editFormData, partyName: e.target.value})} required />
                    </div>
                    <div className="col-md-3">
                      <label className="fw-bold text-muted small mb-1 text-uppercase">PO Number</label>
                      <input type="text" className="form-control fw-bold" value={editFormData.poNumber} onChange={(e) => setEditFormData({...editFormData, poNumber: e.target.value})} />
                    </div>
                    <div className="col-md-4">
                      {/* LOCK: Grand Total ab ReadOnly hai */}
                      <label className="fw-bold text-muted small mb-1 text-uppercase">Grand Total (₹)</label>
                      <input type="number" step="any" className="form-control fw-bold text-primary bg-light" value={editFormData.totalAmount} readOnly />
                    </div>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="fw-bold text-dark mb-0">Extracted Items</label>
                    <button type="button" className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1" onClick={handleAddItemRow}><Plus size={14}/> Add Item</button>
                  </div>

                  <div className="table-responsive rounded-3 border bg-white">
                    <table className="table table-bordered mb-0">
                      <thead className="bg-light text-muted small text-uppercase">
                        <tr>
                          <th>Item Name / Description</th>
                          <th style={{width: '90px'}}>Quantity</th>
                          <th style={{width: '100px'}}>Rate (₹)</th>
                          <th style={{width: '110px'}}>Amount (₹)</th>
                          <th style={{width: '120px'}} title="Manual Override QTY PER ROW">Qty/Row (OR)</th>
                          <th style={{width: '40px'}} className="text-center">Del</th>
                        </tr>
                      </thead>
                      <tbody>
                        {editFormData.items.map((item, index) => (
                          <tr key={index}>
                            <td className="p-1">
                              <input type="text" className="form-control form-control-sm border-0 bg-transparent" value={item.name || item.description || ""} onChange={(e) => handleEditItemChange(index, "name", e.target.value)} required placeholder="Item Name"/>
                            </td>
                            <td className="p-1">
                              <input type="number" step="any" className="form-control form-control-sm border-0 bg-transparent text-center" value={item.qty || ""} onChange={(e) => handleEditItemChange(index, "qty", e.target.value)} placeholder="0"/>
                            </td>
                            <td className="p-1">
                              <input type="number" step="any" className="form-control form-control-sm border-0 bg-transparent text-end" value={item.rate || item.unitPrice || ""} onChange={(e) => handleEditItemChange(index, "rate", e.target.value)} required placeholder="0.00"/>
                            </td>
                            <td className="p-1 bg-light">
                              {/* LOCK: Item Amount ab ReadOnly hai aur auto calculate ho raha hai */}
                              <input type="number" step="any" className="form-control form-control-sm border-0 bg-transparent text-end fw-bold text-primary" value={item.amount || item.total || ""} readOnly placeholder="0.00"/>
                            </td>
                            <td className="p-1 bg-warning bg-opacity-10">
                              <input type="number" step="any" min="0" className="form-control form-control-sm border-0 bg-transparent text-center text-danger fw-bold" value={item.qtyPerRowOverride || ""} onChange={(e) => handleEditItemChange(index, "qtyPerRowOverride", e.target.value)} placeholder="Override"/>
                            </td>
                            <td className="p-1 text-center align-middle">
                              <button type="button" className="btn btn-sm text-danger p-0" onClick={() => handleRemoveItemRow(index)}><X size={16}/></button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="p-4 bg-white border-top d-flex justify-content-end gap-2">
                  <button type="button" className="btn btn-light fw-bold" onClick={() => setShowEditModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary fw-bold px-4" disabled={isUpdating}>{isUpdating ? "Saving..." : "Save Changes"}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ================= UPLOAD MODAL ================= */}
      {showUploadModal && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055}}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
              <div className="modal-header border-0 bg-light p-4">
                <h5 className="fw-black mb-0">AI Bill Scanner</h5>
                <button type="button" className="btn-close" onClick={() => setShowUploadModal(false)}></button>
              </div>
              <form onSubmit={handleUploadSubmit}>
                <div className="modal-body p-4">
                  <label className="d-block border-2 border-dashed rounded-4 p-5 text-center bg-light-hover transition-all position-relative" style={{cursor: 'pointer', borderColor: uploadFile ? '#10b981' : '#dee2e6'}}>
                    <input type="file" className="position-absolute top-0 start-0 w-100 h-100 opacity-0" style={{cursor: 'pointer'}} onChange={(e) => setUploadFile(e.target.files[0])} accept=".pdf,image/*" required />
                    <div className={uploadFile ? 'text-success' : 'text-primary'}>
                      {uploadFile ? (
                        uploadFile.type.startsWith('image/') ? (
                          <div className="mb-3">
                            <img 
                              src={URL.createObjectURL(uploadFile)} 
                              alt="preview" 
                              style={{ maxHeight: '150px', maxWidth: '100%', objectFit: 'contain', borderRadius: '8px' }} 
                            />
                          </div>
                        ) : (
                          <FileText size={48} className="mb-3" />
                        )
                      ) : (
                        <Upload size={48} className="mb-3" />
                      )}
                      <h6 className="fw-bold mb-1">
                        {uploadFile ? (
                          <span className="d-flex align-items-center justify-content-center gap-2">
                            {uploadFile.type.startsWith('image/') ? <Image size={18}/> : <FileText size={18}/>} 
                            {uploadFile.name}
                          </span>
                        ) : "Click or Drag Invoice File"}
                      </h6>
                      {!uploadFile && <p className="text-muted small mb-0">Supports High-res PDF, JPG, PNG </p>}
                    </div>
                  </label>
                  {isUploading && (
                    <div className="mt-4">
                      <div className="d-flex justify-content-between mb-1 small fw-bold">
                        <span className="text-primary">AI is analyzing document...</span>
                        <span className="text-primary">{uploadProgress}%</span>
                      </div>
                      <div className="progress" style={{height: '10px', borderRadius: '10px'}}>
                        <div 
                          className="progress-bar progress-bar-striped progress-bar-animated bg-primary" 
                          role="progressbar" 
                          style={{width: `${uploadProgress}%`, transition: 'width 0.4s ease'}}
                        ></div>
                      </div>
                      <p className="text-muted small text-center mt-2 mb-0">Estimated Time: ~20 seconds</p>
                    </div>
                  )}
                </div>
                <div className="p-4 bg-light d-flex gap-2">
                  <button type="button" className="btn btn-light flex-grow-1 fw-bold" onClick={() => setShowUploadModal(false)} disabled={isUploading}>Cancel</button>
                  <button type="submit" className="btn btn-primary flex-grow-1 fw-bold shadow-sm" disabled={!uploadFile || isUploading}>
                    {isUploading ? "Scanning..." : "Start Analysis"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* OCR QTY MASTER MODAL */}
      <OCRQtyMasterModal show={showOcrQtyModal} onClose={() => setShowOcrQtyModal(false)} />

      {/* SETTINGS / AI PROMPT MODAL */}
      {showSettingsModal && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055}}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content shadow-lg border-0 rounded-4">
              <div className="modal-header bg-light border-bottom-0 py-3">
                <h5 className="modal-title fw-bold text-dark d-flex align-items-center gap-2">
                  <Settings size={20} className="text-primary"/> AI Prompt Configuration
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowSettingsModal(false)}></button>
              </div>
              <div className="modal-body p-4 bg-light">
                <div className="mb-3">
                  <label className="form-label fw-bold text-secondary small text-uppercase">System Prompt</label>
                  <textarea 
                    className="form-control font-monospace border-0 shadow-sm" 
                    rows="12" 
                    value={promptText} 
                    onChange={(e) => setPromptText(e.target.value)}
                    style={{fontSize: '13px', resize: 'none'}}
                  ></textarea>
                  <div className="form-text mt-2 text-muted">This prompt instructs the AI on how to parse the invoice and return JSON. Be careful modifying it.</div>
                </div>
              </div>
              <div className="modal-footer border-top-0 bg-light py-3">
                <button type="button" className="btn btn-white fw-bold px-4" onClick={() => setShowSettingsModal(false)}>Cancel</button>
                <button type="button" className="btn btn-primary fw-bold px-4 shadow-sm" onClick={handleSavePrompt} disabled={isSavingPrompt}>
                  {isSavingPrompt ? <div className="spinner-border spinner-border-sm text-light" role="status"></div> : "Save Prompt"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

/* =========================================================
   UI COMPONENT FOR AI RESULT 
========================================================= */
const InvoiceResultView = ({ ocrResponse, onExport, isExporting }) => {
  if (!ocrResponse) return null;

  const { status, mismatchDetails, data } = ocrResponse;
  const isVerified = status === 'GREEN' || status === 'APPROVED'; 

  // Parse items and taxes correctly
  const itemsList = data?.items?.items || data?.items || [];
  
  // Tax breakdown values (Handling both older and newer JSON structures)
  const taxable = data?.taxableAmount || data?.items?.taxableAmount || 0;
  const cgst = data?.cgst || data?.items?.cgst || 0;
  const sgst = data?.sgst || data?.items?.sgst || 0;
  const igst = data?.igst || data?.items?.igst || 0;
  const grandTotal = data?.totalAmount || 0;

  return (
    <div className="w-100 bg-white rounded-4 shadow-sm border border-light p-4">
      <div className="d-flex justify-content-between align-items-center border-bottom pb-3 mb-4">
        <h4 className="fw-bold mb-0 text-dark">AI Extraction Result</h4>
        <div className="d-flex align-items-center gap-3">
          {status === 'APPROVED' && onExport && (
            <button onClick={() => onExport(ocrResponse.ID)} disabled={isExporting} className="btn btn-sm btn-success px-3 py-2 shadow-sm fw-bold d-flex align-items-center gap-2">
              <DownloadCloud size={16}/> {isExporting ? "Exporting..." : "Export This Bill"}
            </button>
          )}
          <span className={`badge ${status === 'APPROVED' ? 'bg-primary' : (isVerified ? 'bg-success' : 'bg-danger')} px-3 py-2 rounded-pill`} style={{fontSize: '12px'}}>
            {status === 'APPROVED' ? '✅ APPROVED' : (isVerified ? '✅ VERIFIED (GREEN)' : '⚠️ ATTENTION NEEDED (RED)')}
          </span>
        </div>
      </div>

      {!isVerified && mismatchDetails && (
        <div className="alert alert-danger border-start border-danger border-4 rounded-end mb-4" role="alert">
          <h6 className="fw-bold mb-1">Mismatch Found:</h6>
          {/* Mismatch result points yahan line se show honge */}
          {mismatchDetails.split('|').map((err, i) => (
             <div key={i} className="small d-block">• {err.trim()}</div>
          ))}
        </div>
      )}

      <div className="row g-3 mb-4 bg-light p-3 rounded-3 mx-0">
        <div className="col-6 col-md-4">
          <span className="text-muted small text-uppercase fw-bold">Vendor Name</span>
          <div className="fw-bold text-dark">{data?.partyName || 'N/A'}</div>
        </div>
        <div className="col-6 col-md-4">
          <span className="text-muted small text-uppercase fw-bold">PO Number</span>
          <div className="fw-bold text-dark">{data?.poNumber || 'N/A'}</div>
        </div>
        <div className="col-6 col-md-4">
          <span className="text-muted small text-uppercase fw-bold">Bill Date</span>
          <div className="fw-bold text-dark">{data?.date || 'N/A'}</div>
        </div>
      </div>

      <div>
        <h6 className="fw-bold mb-3">Extracted Items</h6>
        <div className="table-responsive rounded-3 border overflow-hidden mb-4">
          <table className="table table-hover mb-0">
            <thead className="bg-dark text-white">
              <tr>
                <th className="text-white small text-uppercase fw-bold py-3 border-0">Item Name / Description</th>
                <th className="text-white small text-uppercase fw-bold text-center py-3 border-0">Qty</th>
                <th className="text-white small text-uppercase fw-bold text-end py-3 border-0">Rate (₹)</th>
                <th className="text-white small text-uppercase fw-bold text-end py-3 border-0">Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              {itemsList.length > 0 ? (
                itemsList.map((item, index) => (
                  <tr key={index}>
                    <td className="align-middle fw-medium text-dark">{item.name || item.description || 'Unknown Item'}</td>
                    <td className="align-middle text-center"><span className="badge bg-light text-dark border px-2 py-1">{item.qty || 0}</span></td>
                    <td className="align-middle text-end text-muted">{Number(item.rate || item.unitPrice || 0).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                    <td className="align-middle text-end fw-bold text-dark">{Number(item.amount || item.total || 0).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center text-muted py-4 fst-italic">No items were extracted by AI.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ================= MISSING TAX CALCULATION BLOCK ADDED BACK ================= */}
        <div className="row justify-content-end mt-3">
          <div className="col-md-5 col-lg-4">
            <div className="bg-light p-3 rounded-3 border">
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted fw-bold">Taxable Amount:</span>
                <span className="fw-bold text-dark">₹{Number(taxable).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted small">CGST:</span>
                <span className="text-muted small">₹{Number(cgst).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted small">SGST:</span>
                <span className="text-muted small">₹{Number(sgst).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
              </div>
              <div className="d-flex justify-content-between mb-3">
                <span className="text-muted small">IGST:</span>
                <span className="text-muted small">₹{Number(igst).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
              </div>
              <div className="d-flex justify-content-between border-top pt-2">
                <span className="text-dark fw-bolder">Grand Total:</span>
                <span className="text-primary fw-bolder fs-5">₹{Number(grandTotal).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
              </div>
            </div>
          </div>
        </div>
        {/* ======================================================================= */}

      </div>
    </div>
  );
};